import styled from '@emotion/styled';
import {Col, Container, media, Row, Spacer, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H1, Label} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {useHero} from 'UI/modules/Blocks/Hero/useHero';

import {transient} from 'Application/configs/emotionTransientProps';
import {LogoMnet, LogoSwm, LogoTelekom} from 'Images/icons';
import {splitText} from 'UI/utils/helpers';

import type {HeroType} from 'types/payload-types';

/**
 * The props for the `Hero` component, which renders a prominent hero section
 * with headline, description, CTA button, partner logos, and an optional image.
 * This interface defines the data required to render the hero block, including
 * its unique identifier, content fields, and test attributes for automation.
 */
interface ComponentProps {
    /**
     * Optional configuration for the call-to-action (CTA) button.
     * Defines the link, label, and behavior of the button.
     */
    button?: HeroType['button'];
    /**
     * The main headline text displayed in the hero section.
     * Rendered as an `<h1>` element for semantic and accessibility purposes.
     */
    headline?: HeroType['headline'];
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: HeroType['hideBlock'];
    /**
     * The unique identifier for the hero block, used for linking and accessibility.
     * This ID is essential for in-page navigation and should be unique across the page.
     */
    id: HeroType['id'];
    /**
     * Optional image displayed alongside the hero content.
     * Typically used for an illustration, product image, or supporting graphic.
     */
    image?: HeroType['image'];
    /**
     * Optional overline text displayed above the main headline.
     * Often used to provide context or a category label.
     */
    overline?: HeroType['overline'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Hero'
     */
    testId?: string;
    /**
     * Optional descriptive text rendered below the headline.
     * Supports rich text formatting through the `PayloadLexical` component.
     */
    text?: HeroType['text'];
}

/**
 * A hero section component that displays a headline, description, CTA button, and an image with partner logos.
 * The `Hero` component provides a prominent introductory section, typically at the top of a page.
 * It includes an optional overline, a main headline, descriptive text, a call-to-action button,
 * and an associated image. Partner logos are also displayed below the CTA to emphasize credibility
 * or collaborations. Layout and spacing are responsive, ensuring proper presentation across screen sizes.
 * The `useHero` hook is used to resolve the image dimensions and CTA button link attributes
 * (`href`, `label`, `rel`, `target`), ensuring consistent link behavior. The headline is split
 * with `splitText` for styling and accessibility purposes.
 * The component is wrapped with MobX’s `observer`, allowing it to re-render when observable data changes.
 * A `testId` prop is applied as a `data-cy` attribute for end-to-end testing.
 *
 * @param props           The component props.
 * @param props.button    Optional link configuration for the call-to-action button.
 * @param props.headline  The main headline displayed as an `<h1>`, split for visual styling.
 * @param props.hideBlock An optional flag to hide the entire block when set to true.
 * @param props.id        The unique identifier applied to the headline for accessibility and linking.
 * @param props.image     Optional image configuration displayed alongside the content.
 * @param props.overline  Optional overline text displayed above the headline.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'Hero'`.
 * @param props.text      Optional descriptive text, rendered as rich text via `PayloadLexical`.
 * @returns A JSX element rendering the hero section with headline, CTA, partner logos, and an image.
 *
 * @example
 * ```tsx
 * <Hero
 *   id="hero-section"
 *   overline="Welcome to our Service"
 *   headline="Fast, Reliable, Affordable"
 *   text={heroText}
 *   button={{ label: "Get Started", linkType: "internal", internal: "/start" }}
 *   image={heroImage}
 * />
 * ```
 */
const Hero = observer(({
    button,
    headline,
    hideBlock = false,
    id,
    image,
    overline,
    testId = 'Hero',
    text
}: ComponentProps) => {
    const {height, href, label, rel, target, width} = useHero(button, image);

    return hideBlock ? null : (
        <ViewportTrigger baseComponent={Container} testId={testId}>
            <Spacer y={{
                xs: 0, lg: 12
            }}
            />
            <Row align="center" justify="space-between">
                <Col xl="auto" xs={12}>
                    <ContentWrapper>
                        {overline && <MotionLabel variants={FadeInAnimation}>{overline}</MotionLabel>}
                        <Spacer y={4} isNotStretching />
                        {headline && <MotionH1 id={id!} variants={FadeInAnimation}>{splitText(headline)}</MotionH1>}
                        <Spacer y={10} isNotStretching />
                        <TextWrapper variants={FadeInAnimation}>
                            { }
                            {text && <PayloadLexical description={text} />}
                        </TextWrapper>
                        <Spacer y={8} isNotStretching />
                        <MotionButton
                            as="link"
                            href={href}
                            rel={rel}
                            target={target}
                            variants={FadeInAnimation}
                        >
                            {label}
                        </MotionButton>
                        <Spacer
                            y={{
                                xs: 16, lg: 20
                            }}
                            isNotStretching
                        />
                        <PartnerList variants={FadeInAnimation}>
                            <Label>Partner</Label>
                            <Visible xl xxl>
                                <Divider />
                            </Visible>
                            <LogoWrapper>
                                <LogoSwm />
                                <LogoMnet />
                                <LogoTelekom />
                            </LogoWrapper>
                        </PartnerList>
                    </ContentWrapper>
                </Col>
                <Col align="center" justify="center" xl={6} xs={12}>
                    <ImageWrapper $height={height} $width={width} variants={FadeInAnimation}>
                        {image && (
                            <Image
                                expectedSizes={{
                                    xs: 272, sm: 500, md: 500, lg: 464, xl: 575, xxl: 684
                                }}
                                fetchpriority="high"
                                image={image}
                                isLazy={false}
                            />
                        )}
                    </ImageWrapper>
                </Col>
            </Row>
        </ViewportTrigger>
    );
});

Hero.displayName = 'Hero';

export {Hero};

const ContentWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    padding-block-end: ${spacing(18)};
    padding-block-start: ${spacing(12)};

    p, h1 {
        text-align: center;
    }

    ${media('xl')} {
        align-items: start;
        max-width: 56.7rem;
        min-width: 0;
        padding-block-end: 0;
        padding-block-start: ${spacing(6)};

        p, h1 {
            text-align: left;
        }
    }
`;

const MotionLabel = styled(motion.create(Label))``;

const MotionH1 = styled(motion.create(H1))``;

const TextWrapper = styled(motion.div)`
    ${media('xl')} {
        padding-inline-end: ${spacing(20)};
    }
`;

const MotionButton = styled(motion.create(Button))``;

const PartnerList = styled(motion.div)`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};

    ${media('xl')} {
        flex-direction: row;
        gap: ${spacing(10)};
    }
`;

const Divider = styled.div`
    background-color: ${({theme}) => theme.colors.neutral200};
    height: 3.2rem;
    width: 1px;
`;

const LogoWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(10)};
`;

interface ImageWrapperProps {
    $height?: number;
    $width?: number;
}

const ImageWrapper = styled(motion.div, transient)<ImageWrapperProps>`
    aspect-ratio: ${({$height, $width}) => `${$width} / ${$height}`};
    border-radius: 12px;
    height: 100%;
    max-width: 60rem;
    overflow: hidden;
    width: 100%;

    ${media('xl')} {
        max-height: 68.1rem;
        max-width: initial;
        width: 100%;
    }
`;

const Image = styled(PayloadImage)`
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    min-height: 0;
    min-width: 0;
    object-fit: contain;
    width: 100%;
`;